// apps/landing-page/src/components/search/CatalogSearchHeader.tsx
// The search box and exam family tiles under the /tests h1. No 'use client' on purpose:
// the server shell (tests/page.tsx) and the client catalog render this same markup, so
// nothing moves when the client takes over. The shell passes no onQueryChange.
import Link from 'next/link';
import { LayoutGrid } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { examIcon } from '@/lib/exam-icons';
import type { ExamType } from '@/lib/api';
import { PageSearchInput } from './PageSearchInput';

interface CatalogSearchHeaderProps {
  query: string;
  onQueryChange?: (value: string) => void;
  examTypes: ExamType[];
  activeSlug?: string;
  /** Link for a tile; null is the "all" tile. */
  hrefFor: (slug: string | null) => string;
}

const TILE = 'flex min-w-[88px] flex-shrink-0 flex-col items-center gap-1.5 rounded-2xl bg-white px-3 py-3 transition-colors hover:bg-teal-50';

export function CatalogSearchHeader({ query, onQueryChange, examTypes, activeSlug, hrefFor }: CatalogSearchHeaderProps) {
  const t = useTranslations('search');
  const tCatalog = useTranslations('testsCatalog');
  const tiles = examTypes.filter((et) => et._count.modules > 0);

  return (
    <>
      <PageSearchInput value={query} onChange={onQueryChange} label={t('pageInputLabel')} placeholder={t('pagePlaceholder')} />

      {tiles.length > 0 && (
        <nav aria-label={t('familiesLabel')} className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 scrollbar-hide sm:mx-0 sm:flex-wrap sm:px-0">
          <Link
            href={hrefFor(null)}
            replace
            scroll={false}
            aria-current={!activeSlug ? 'true' : undefined}
            className={`${TILE} ${!activeSlug ? 'ring-2 ring-orange-400' : ''}`}
          >
            <LayoutGrid aria-hidden="true" className="h-6 w-6 text-teal-700" />
            <span className="text-center text-xs font-semibold leading-tight text-slate-700">{tCatalog('semua')}</span>
          </Link>
          {tiles.map((et) => {
            const Icon = examIcon(et.slug);
            const isActive = activeSlug === et.slug;
            return (
              <Link
                key={et.slug}
                // Tapping the active tile clears the filter, like the chips this replaces.
                href={hrefFor(isActive ? null : et.slug)}
                replace
                scroll={false}
                aria-current={isActive ? 'true' : undefined}
                className={`${TILE} ${isActive ? 'ring-2 ring-orange-400' : ''}`}
              >
                <Icon aria-hidden="true" className="h-6 w-6" style={{ color: et.color ?? '#0d9488' }} />
                <span className="text-center text-xs font-semibold leading-tight text-slate-700">{et.name}</span>
              </Link>
            );
          })}
        </nav>
      )}
    </>
  );
}
